import { useMemo } from 'react'
import searchItems from 'const/searchItems'
import normalizeFeatureContext from 'helpers/featureContext'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import i18n from 'strings/i18n'
import { type SearchItem, isSearchItemEligible } from 'const/searchItems'
import useAccountContexts, { getAggregate } from './useAccountContexts'
import type { ResolvedSearchItem } from './useSemanticSearch'

export interface SearchItemsResult {
  items: ResolvedSearchItem[]
  isLoading: boolean
  isError: boolean
}

const splitTags = (value: string): string[] =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)

// Flag off + ivaFallback still surfaces the item; click-time deflects to IVA.
const passesFlagGate = (item: SearchItem) =>
  !item.featureFlag ||
  Boolean(item.ivaFallback) ||
  isFeatureFlagEnabled(item.featureFlag)

const isLwsSession = () => Boolean(sessionStorage.getItem('lwsRedirectUri'))

/**
 * The catalog filtered to what the signed-in customer can actually reach, with
 * label and tags resolved in the active language.
 */
const useSearchItems = (): SearchItemsResult => {
  const { accounts, isLoading, isError } = useAccountContexts()
  const language = getLanguageFromStorage()

  const items = useMemo(
    () => {
      const contexts = accounts.map(({ context }) => context)
      const aggregate = getAggregate(contexts)
      // Normalize once per account; the filter below is then O(items × accounts) boolean checks.
      const featureContexts = contexts.map((context) =>
        normalizeFeatureContext(context, aggregate)
      )
      const isLws = isLwsSession()
      return searchItems
        .filter((item) => {
          if (isLws && item.isHiddenForLws) return false
          if (!passesFlagGate(item)) return false
          if (!item.isAccountScoped) return true
          return featureContexts.some((ctx) => isSearchItemEligible(item, ctx))
        })
        .map((item) => ({
          ...item,
          label: String(i18n(item.labelKey)),
          tags: splitTags(String(i18n({ searchTags: item.id })))
        }))
    },
    // `language` looks unused but i18n() reads it via storage — keep it so the memo recomputes on switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, language]
  )

  return { items, isLoading, isError }
}

export default useSearchItems
